import { Alert, App, Form, Input, Modal } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { createUser, type PanelUser } from './identityApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface UserFormModalProps {
  open: boolean
  onClose: () => void
  /** Yaratilgandan keyin — chaqiruvchi keyingi qadamga o'tkazadi */
  onCreated: (user: PanelUser) => void
}

interface FormValues {
  username: string
  rawPassword: string
  displayName?: string
}

/**
 * Yangi XODIM hisobi.
 *
 * ⚠️ Parol shu yerda BIR MARTA kiritiladi va hech qachon qaytarilmaydi —
 * na bu formada, na ro'yxatda, na kartochkada (CLAUDE.md §7, backendda
 * `PasswordRequest` javadoc'i). Shuning uchun `Input.Password` ning
 * "ko'zcha" tugmasi ham OLIB TASHLANGAN: operator o'zi kiritayotgan
 * parolni ekranda ochiq ko'rsatib, yonidagi odamga ko'rsatib qo'ymasin.
 *
 * ⚠️ Naqsh va uzunlik backend `CreateUserCommand` bilan AYNAN bir xil.
 * Farq bo'lsa operator "Internal Server Error" ko'rardi va xato o'zining
 * kiritganida ekanini bilmasdi.
 *
 * <h2>⚠️ FAQAT XODIM — SINF TANLANMAYDI</h2>
 * Egasi qarori (2026-08-19): agent va reseller Agentlar/Resellerlar
 * sahifasidagi YAGONA forma bilan qo'shiladi. Shuning uchun bu yerda
 * sinf, sherik turi va kassa bayrog'i maydonlari YO'Q: operator faqat
 * xodim yaratadi va unga rol beradi. Sherik hisoblari baribir
 * `identity.app_user` da yashaydi, lekin ularning sozlamalari bu
 * sahifadan BERILMAYDI — ikki joydan bir xil narsani sozlash
 * operatorni adashtirardi.
 */
export function UserFormModal({ open, onClose, onCreated }: UserFormModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  const create = useMutation({
    mutationFn: (values: FormValues): Promise<PanelUser> =>
      createUser({
        username: values.username.trim(),
        rawPassword: values.rawPassword,
        ...(values.displayName?.trim() ? { displayName: values.displayName.trim() } : {}),
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.identity.users })
      // X-SIB-004 — muvaffaqiyatli amaldan keyin ANIQ tasdiq: oyna
      // yopiladi va ekranda hech qanday iz qolmasdi
      void message.success(t('common.toastCreated'))
      form.resetFields()
      onClose()
      onCreated(created)
    },
  })

  const close = () => {
    create.reset()
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('identity.create.title')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => create.mutate(values))}
      confirmLoading={create.isPending}
      okText={t('operation.create')}
      cancelText={t('common.cancel')}
      destroyOnHidden
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {create.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(create.error, t)}
        />
      ) : null}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('identity.create.noRoleYet')}
      />

      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="username"
          label={t('identity.columns.username')}
          rules={[
            { required: true, message: t('identity.create.usernameRequired') },
            { pattern: /^[a-zA-Z0-9._@-]{3,64}$/, message: t('identity.create.usernameFormat') },
          ]}
        >
          <Input autoFocus autoComplete="off" />
        </Form.Item>
        <Form.Item name="displayName" label={t('identity.columns.displayName')}>
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="rawPassword"
          label={t('identity.create.password')}
          extra={t('identity.create.passwordHint')}
          rules={[
            { required: true, message: t('identity.create.passwordRequired') },
            { min: 12, max: 72, message: t('identity.create.passwordLength') },
          ]}
        >
          {/* `visibilityToggle={false}` — yuqoridagi izohga qarang */}
          <Input.Password visibilityToggle={false} autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
